/**
 * "How to play" (pause menu, title, and the book's HOW TO PLAY tab): an illustrated broadsheet page in
 * the game's print style. The core loop as a little flow strip, the controls drawn as the actual key caps
 * (desktop) or the actual Bakelite knobs (touch), the heat badges, and where the book and the bleeper are.
 * Pure DOM; no game state beyond the bindings and the touch flag.
 */
import './guide.css';
import { el, esc, html, scrollCue } from './dom';
import { HEAT_NAMES, keyLabel } from './format';
import { CONTROL_ICONS, HEAT_ICONS, type ControlIcon } from './icons';
import type { NavAction, Screen, ScreenHost } from './screens';

/** The core loop, left to right, looping back. */
export const HELP_LOOP: readonly { head: string; text: string }[] = [
  { head: 'Take a job', text: 'Mags at the Crooked Anchor, a ringing phone box, Halloran’s export orders.' },
  { head: 'Nick a motor', text: 'Anything at the kerb. Locked ones take a moment; drivers can be pulled out.' },
  { head: 'Do the job', text: 'Follow the red marker. The clipping top-left says what’s next.' },
  { head: 'Lose the law', text: 'Corners, alleys, distance. Heat cools once they lose you.' },
  { head: 'Get paid', text: 'Cash goes on Pike’s respray (loses the police), the lockup key, Sid’s guns and Des’s workshop.' },
];

/** How each heat level is shaken off (one line each, matches docs/POLICE.md in spirit). */
const HEAT_LINES = [
  'Nobody is looking. Keep it that way and walk past the coppers.',
  'A copper took an interest. Get out of sight and it fades.',
  'They are after you. Break line of sight round a corner.',
  'Roadblocks on the main roads. Use the alleys and the bridges.',
  'Every unit in the borough. Swap motors where nobody sees, or the paint shop.',
];

type Row = [label: string, keys: string[] | ControlIcon[], note?: string];

export class HelpScreen implements Screen {
  readonly root: HTMLElement;
  private readonly sheet: HTMLElement;

  constructor(parent: HTMLElement, host: ScreenHost, private readonly onBack: () => void) {
    const touch = host.touch?.() ?? false;
    this.root = el('div', 'bh-screen bh-help', undefined, parent);
    this.sheet = el('div', 'sheet bh-paper', undefined, this.root);
    scrollCue(this.sheet)(); // a torn-edge fade at the foot of the broadsheet while there is more to read
    this.sheet.dataset.touchScroll = '';
    const head = el('div', 'mast', undefined, this.sheet);
    html('span', '', '<b>THE BOROUGH GAZETTE</b> · A BEGINNER’S GUIDE', head);
    const close = el('button', 'close bh-x', '✕', head);
    close.type = 'button';
    close.setAttribute('aria-label', 'Close');
    close.addEventListener('click', (e) => {
      e.stopPropagation();
      host.sound('back');
      this.onBack();
    });
    el('h2', '', 'How to play', this.sheet);
    el(
      'p',
      'lede',
      'You’re new on the manor with forty quid and a bad name. Work for Mags Delaney’s firm, nick motors, keep the law off your back, and spend what you earn on staying ahead.',
      this.sheet,
    );

    // The loop.
    const loop = el('ol', 'loop', undefined, this.sheet);
    HELP_LOOP.forEach((s, i) => {
      const li = el('li', '', undefined, loop);
      el('b', 'n', String(i + 1), li);
      el('strong', '', s.head, li);
      el('span', '', s.text, li);
    });

    const cols = el('div', 'cols', undefined, this.sheet);
    const b = host.bindings();
    const k = (codes: string[]): string[] => [...new Set(codes.map(keyLabel))];
    const foot: Row[] = touch
      ? [
          ['Walk', [], 'Drag low on the left. The stick floats to your thumb.'],
          ['Run', ['run'], 'Hold.'],
          ['Get in · steal · talk', ['use'], 'Stand by the door or the person.'],
          ['Hit · aim · swap', ['aim', 'swap'], 'HIT is the big knob in the corner.'],
        ]
      : [
          ['Walk', ['W', 'A', 'S', 'D']],
          ['Run', k(b.keys.run), 'Hold.'],
          ['Talk · use a place', k(b.keys.enterExit).slice(0, 1)],
          ['Get in · steal a car', k(b.keys.vehicle).slice(0, 1), 'Always the car, even next to a phone box.'],
          ['Hit · aim · swap', [...k(b.keys.attack).slice(0, 1), ...k(b.keys.aim).slice(0, 1), ...k(b.keys.weaponNext).slice(0, 1)]],
        ];
    const car: Row[] = touch
      ? [
          ['Steer', [], 'Point the stick where you want to go.'],
          ['Gas · brake', ['gas', 'brake'], 'Hold BRAKE: stop, then back up where the stick points.'],
          ['Handbrake', ['handbrake'], 'Slide round corners.'],
          ['Get out · horn · radio', ['out', 'horn', 'radio']],
        ]
      : [
          ['Gas · brake / reverse', ['W', 'S']],
          ['Steer', ['A', 'D']],
          ['Handbrake', k(b.keys.handbrake), 'Slide round corners.'],
          ['Get out · horn · radio', [...k(b.keys.vehicle).slice(0, 1), ...k(b.keys.horn), ...k(b.keys.radio)]],
        ];
    const always: Row[] = touch
      ? [
          ['Your book', ['book'], 'Jobs, messages, contacts — top right.'],
          ['Full map', ['map']],
          ['Pause · settings', ['pause']],
        ]
      : [
          ['Your book', ['I'], 'Jobs, messages, contacts. The bleeper top right counts new ones.'],
          ['Full map', k(b.keys.map)],
          ['Pause · settings', k(b.keys.pause)],
        ];
    this.section(cols, 'On foot', foot, touch);
    this.section(cols, 'At the wheel', car, touch);
    this.section(cols, 'Any time', always, touch);

    // The law.
    const law = el('div', 'law', undefined, this.sheet);
    el('h3', '', 'The law', law);
    const ul = el('ul', '', undefined, law);
    HEAT_NAMES.forEach((name, i) => {
      const li = el('li', `lvl${i}`, undefined, ul);
      html('span', 'badge', HEAT_ICONS[i]!, li);
      html('span', '', `<b>${esc(name)}</b> ${esc(HEAT_LINES[i]!)}`, li);
    });
    el(
      'p',
      'small',
      'Stop next to a police car with an officer at your door and you’re nicked: a fine, and the motor goes to the pound. Get hurt badly and you wake up in hospital with a bill.',
      law,
    );

    const online = el('div', 'online', undefined, this.sheet);
    el('h3', '', 'Online', online);
    el(
      'p',
      '',
      touch
        ? 'The same borough, shared. Jobs boards, the bank, gangs, crews, freight and premises — open your book (top right) or MENU; shops and counters open with USE at the door.'
        : 'The same borough, shared. Jobs boards, the bank, gangs, crews, freight and premises — your book (I) lists every letter key: B pockets, J job, G gang, T chat, C crew, X freight. Shops and counters open with E at the door.',
      online,
    );

    html('div', 'bh-foot', touch ? '<span>Swipe to read · ✕ closes</span>' : '<span>↑ ↓ scroll</span><span>Esc / Enter back</span>', this.sheet);
  }

  private section(parent: HTMLElement, title: string, rows: Row[], touch: boolean): void {
    const box = el('div', 'sec', undefined, parent);
    el('h3', '', title, box);
    const dl = el('dl', '', undefined, box);
    for (const [label, keys, note] of rows) {
      el('dt', '', label, dl);
      const dd = el('dd', '', undefined, dl);
      if (touch) {
        for (const ic of keys as ControlIcon[]) html('span', 'knob', CONTROL_ICONS[ic] ?? '', dd);
        if (keys.length === 0) html('span', 'knob stick', '<i></i>', dd);
      } else for (const key of keys as string[]) el('kbd', '', key, dd);
      if (note) el('small', '', note, dd);
    }
  }

  onNav(a: NavAction): boolean {
    if (a === 'back' || a === 'confirm') {
      this.onBack();
      return true;
    }
    if (a === 'up' || a === 'down') {
      this.sheet.scrollBy({ top: (a === 'up' ? -1 : 1) * 120, behavior: 'smooth' });
      return true;
    }
    return true;
  }

  dispose(): void {
    this.root.remove();
  }
}
